// The manifest of a batch (LH-F5): which endpoints its src/api/ folder (or api/) defines, with their outcomes, and
// which variables the code reads. Nobody writes it by hand. The truth is `hub-discover` in the sandbox
// (firstfund-lender-hub/sdk, it loads every file); this file is the same reading done on the text alone, for the demo
// data and for when the agent is offline. Both give the same shape; the portal stores whichever it got last on
// `batch.manifest`.
import type { CodeFile, HttpMethod, Manifest, ManifestEndpoint } from '@/types'

const SKIP = /^(node_modules|\.git|\.hub|tests)(\/|$)/
const KNOWN = ['ok', 'offer', 'approved', 'redirect', 'pending', 'rejected', 'duplicate', 'error']

/** The standard layout (owner, 2026-10-17): src/server.js, src/app.js, src/api/<route>.js, src/services/. `api/` at the root is still read. */
export const API_ROOT = 'src/api'
const API_RE = /^(?:src\/)?api\//
export const isApiPath = (p: string) => API_RE.test(p)

export const isCode = (path: string) => /\.(c?js|mjs)$/.test(path)
export const isApiFile = (path: string) => isApiPath(path) && isCode(path) && !SKIP.test(path)

/** src/api/v2.0.0/get-offer.js → /v2.0.0/get-offer; api/index.js → /; src/api/v1/lead/index.js → /v1/lead (as discover.js). */
export function routeOf(file: string): string {
  const noExt = file.replace(API_RE, '').replace(/\.(c?js|mjs)$/, '')
  return '/' + noExt.split('/').filter((p) => p !== 'index').join('/')
}

const ENV_RE = /(?:ctx\.env|process\.env)\.([A-Z_][A-Z0-9_]*)|ctx\.secret\(\s*['"`]([A-Z_][A-Z0-9_]*)['"`]\s*\)/g
/** the service's own variables (src/server.js reads PORT): set by the runner, never a lender variable in tab 5 */
export const RUNTIME_ENV = new Set(['PORT', 'HUB_ENVIRONMENT', 'NODE_ENV'])

export function envNamesIn(source: string): string[] {
  const out: string[] = []
  for (const m of source.matchAll(ENV_RE)) {
    const n = (m[1] ?? m[2])!
    if (!RUNTIME_ENV.has(n) && !out.includes(n)) out.push(n)
  }
  return out
}

/** Reads defineApi({ … }) from the text: outcomes, method, title, tokenSource. Null when the file defines no endpoint. */
export function readDefineApi(source: string): { outcomes: string[]; method: HttpMethod; title: string | null; tokenSource: boolean; line: number } | null {
  const m = /defineApi\s*\(\s*\{/.exec(source)
  if (!m || !/module\.exports\s*=\s*defineApi|export\s+default\s+defineApi/.test(source)) return null
  // the options before handler(): method / outcomes / title / tokenSource; the handler's own ctx.lender({ method }) must not count
  const body = source.slice(m.index)
  const h = /[\r\n]\s*(?:async\s+)?handler\s*[(:]/.exec(body)
  const head = body.slice(0, h ? h.index : 1200)
  const oc = /outcomes\s*:\s*\[([^\]]*)\]/.exec(head)
  const outcomes = oc ? Array.from(oc[1]!.matchAll(/['"`]([a-z_]+)['"`]/g), (x) => x[1]!) : []
  const method = (/method\s*:\s*['"`]([A-Z]+)['"`]/.exec(head)?.[1] ?? 'POST') as HttpMethod
  const title = /title\s*:\s*['"`]([^'"`]*)['"`]/.exec(head)?.[1] ?? null
  const tokenSource = /tokenSource\s*:\s*true/.test(head)
  return { outcomes: Array.from(new Set([...outcomes.filter((o) => o !== 'error'), 'error'])), method, title, tokenSource, line: source.slice(0, m.index).split('\n').length }
}

/** The manifest from the files' text (the demo data, the offline fallback). The agent's hub-discover is the truth. */
export function staticDiscover(files: CodeFile[], when = '2026-10-17T00:00:00+05:30'): Manifest {
  const endpoints: ManifestEndpoint[] = []
  const problems: Manifest['problems'] = []
  const env = new Map<string, string[]>()
  for (const f of files.filter((x) => isCode(x.path) && !SKIP.test(x.path)).sort((a, b) => a.path.localeCompare(b.path))) {
    for (const n of envNamesIn(f.content)) env.set(n, [...(env.get(n) ?? []), f.path])
    if (!isApiFile(f.path)) continue
    const d = readDefineApi(f.content)
    if (!d) {
      problems.push({ file: f.path, text: 'defines no endpoint: module.exports = defineApi({ outcomes, handler })' })
      continue
    }
    const bad = d.outcomes.filter((o) => !KNOWN.includes(o))
    if (bad.length) problems.push({ file: f.path, text: `outcomes ${bad.join(', ')} are not ones the system knows` })
    endpoints.push({ path: routeOf(f.path), method: d.method, file: f.path, outcomes: d.outcomes, title: d.title, tokenSource: d.tokenSource })
  }
  const seen = new Set<string>()
  for (const e of endpoints) {
    const k = `${e.method} ${e.path}`
    if (seen.has(k)) problems.push({ file: e.file, text: `route ${k} is defined twice` })
    seen.add(k)
  }
  let dependencies: Record<string, string> = {}
  try {
    const pkg = JSON.parse(files.find((f) => f.path === 'package.json')?.content ?? '{}') as { dependencies?: Record<string, string> }
    dependencies = pkg.dependencies ?? {}
  } catch { /* a package.json being typed */ }
  return { generatedAt: when, source: 'static', endpoints, env: Array.from(env, ([name, fs]) => ({ name, files: Array.from(new Set(fs)) })), dependencies, problems }
}

export const emptyManifest = (): Manifest => ({ generatedAt: '', source: 'static', endpoints: [], env: [], dependencies: {}, problems: [] })
